import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useApiContext, API_BASE, handleResponse } from "@/contexts/ApiContext";
import { ACCOUNT_CLOSED_EVENT } from "@/lib/accountClosed";
import { useAuth } from "@/auth";
import { testIds } from "@/testIds";

const T = testIds.accountClosed;

/**
 * A closed account signing in again within its 30 days (launch redesign 3g): any request that hears
 * 403 ACCOUNT_CLOSED brings up this screen, with Restore or Sign out.
 */
export function AccountClosedGate() {
  const { t, i18n } = useTranslation("auth");
  const { fetchWithAuth } = useApiContext();
  const { signOut } = useAuth();
  const [closed, setClosed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const on = () => setClosed(true);
    window.addEventListener(ACCOUNT_CLOSED_EVENT, on);
    return () => window.removeEventListener(ACCOUNT_CLOSED_EVENT, on);
  }, []);
  const status = useQuery({
    queryKey: ["me", "account"],
    queryFn: async () => handleResponse<{ erasedOn: string | null }>(await fetchWithAuth(`${API_BASE}/me/account`)),
    enabled: closed,
  });
  if (!closed) return null;

  const restore = async () => {
    setBusy(true);
    const r = await fetchWithAuth(`${API_BASE}/me/restore`, { method: "POST" }).catch(() => null);
    if (r?.ok) window.location.reload();
    else { setBusy(false); setFailed(true); }
  };
  const date = status.data?.erasedOn
    ? new Date(status.data.erasedOn).toLocaleDateString(i18n.language, { day: "numeric", month: "long", year: "numeric" })
    : "";

  return (
    <div data-testid={T.gate} role="dialog" aria-modal="true" className="fixed inset-0 z-[100] flex items-center justify-center bg-[var(--lilia-bg)] p-6">
      <div className="max-w-[440px] rounded-[14px] border border-[var(--lilia-border)] bg-[var(--lilia-surface)] p-6 text-center">
        <h1 className="text-[19px] font-semibold text-[var(--lilia-text)]">{t("accountClosed.title")}</h1>
        <p className="mt-2 text-[14px] leading-[1.55] text-[var(--lilia-text-secondary)]">{t("accountClosed.body", { date })}</p>
        {failed && <p role="alert" className="mt-2 text-[12.5px] text-[var(--lilia-danger,#c0362c)]">{t("accountClosed.error")}</p>}
        <div className="mt-5 flex justify-center gap-2">
          <button type="button" data-testid={T.signOut} onClick={() => void signOut()} className="h-9 rounded-[9px] border border-[var(--lilia-border)] px-4 text-[13px] text-[var(--lilia-text)]">
            {t("accountClosed.signOut")}
          </button>
          <button type="button" data-testid={T.restore} disabled={busy} onClick={() => void restore()} className="h-9 rounded-[9px] bg-[var(--lilia-primary)] px-4 text-[13px] font-semibold text-[color:var(--lilia-on-primary)] disabled:opacity-50">
            {busy ? t("accountClosed.restoring") : t("accountClosed.restore")}
          </button>
        </div>
      </div>
    </div>
  );
}
